import React, { useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { gql, useMutation } from "@apollo/client";
import {
  GlobalDispatchContext,
  GlobalStateContext,
} from "../context/global-context-provider";
import { updateDataLayer } from "../utils/helpers";
import { graphql, navigate, useStaticQuery } from "gatsby";
import { useLocation } from "@reach/router";

export const klarnaSnippets = (ref, htmlSnippet) => {
  const container = ref.current;
  container.innerHTML = htmlSnippet;
  var scriptsTags = container.getElementsByTagName("script");
  // This is necessary otherwise the scripts tags are not going to be evaluated
  for (var i = 0; i < scriptsTags.length; i++) {
    var parentNode = scriptsTags[i].parentNode;
    var newScriptTag = document.createElement("script");
    newScriptTag.type = "text/javascript";
    newScriptTag.text = scriptsTags[i].text;
    parentNode.removeChild(scriptsTags[i]);
    parentNode.appendChild(newScriptTag);
  }
};

const KlarnaCheckout = () => {
  const klarnaRef = useRef();

  return (
    <>
      <div
        ref={klarnaRef}
        dangerouslySetInnerHTML={{ __html: null }}
        style={{ width: "100%" }}
      />
      <KlarnaCheckoutHooks klarnaRef={klarnaRef} />
    </>
  );
};

const KlarnaCheckoutHooks = ({ klarnaRef }) => {
  const {
    wp: {
      wcSettings: { checkoutUrl },
    },
  } = useStaticQuery(graphql`
    query {
      wp {
        wcSettings {
          checkoutUrl
        }
      }
    }
  `);
  const [checkout] = useMutation(CHECKOUT);
  const [payOrder] = useMutation(PAY_ORDER);
  const [getKlarnaCheckoutOrder] = useMutation(GET_ORDER);
  const { cartExternalRevision, cart } = useContext(GlobalStateContext);
  const dispatch = useContext(GlobalDispatchContext);
  const location = useLocation();

  const [createKlarnaCheckoutOrder] = useMutation(CREATE_KLARNA_CHECKOUT_ORDER);

  function convertAndSumPrice(price1, price2) {
    const num1 = parseFloat(price1.replace(/[^0-9,]/g, "").replace(",", "."));
    const num2 = parseFloat(price2.replace(/[^0-9,]/g, "").replace(",", "."));
    return num1 + num2;
  }

  const beginCheckoutEvent = (shippingCost) => {
    updateDataLayer(async () =>
      window.dataLayer.push({
        event: "begin_checkout",
        ecommerce: {
          currency: cart?.[0]?.currency,
          shipping: shippingCost,
          value: cart.reduce(
            (acc, item) => acc + item.priceRaw * item.quantity,
            0
          ),
          items: cart.map((item) => ({
            item_id: item?.id,
            item_name: item?.name,
            item_category: item?.category,
            item_brand: item?.brand,
            price: item?.priceRaw,
            quantity: item?.quantity,
          })),
        },
      })
    );
  };

  const displayCheckout = (htmlSnippet) => {
    klarnaSnippets(klarnaRef, htmlSnippet);
    window._klarnaCheckout((api) =>
      api.on({
        validation_callback: (_, callback) => {
          getKlarnaCheckoutOrder()
            .then(
              ({
                data: {
                  getKlarnaCheckoutOrder: {
                    billingAddress,
                    shippingAddress,
                    selectedShippingId,
                  } = {},
                },
              }) => {
                return checkout({
                  variables: {
                    input: {
                      paymentMethod: "kco",
                      billing: {
                        firstName: billingAddress?.givenName,
                        lastName: billingAddress?.familyName,
                        address1: billingAddress?.streetAddress,
                        city: billingAddress?.city,
                        country: billingAddress?.country.toUpperCase(),
                        postcode: billingAddress?.postalCode,
                        email: billingAddress?.email,
                        phone: billingAddress?.phone,
                      },
                      shipping: {
                        firstName: shippingAddress?.givenName,
                        lastName: shippingAddress?.familyName,
                        address1: shippingAddress?.streetAddress,
                        city: shippingAddress?.city,
                        country: shippingAddress?.country.toUpperCase(),
                        postcode: shippingAddress?.postalCode,
                        email: shippingAddress?.email,
                        phone: shippingAddress?.phone,
                      },
                      shippingMethod: selectedShippingId,
                    },
                  },
                });
              }
            )
            .then((data) => {
              const order = data.data.checkout.order;
              const shippingCost = convertAndSumPrice(
                order.shippingTotal,
                order.shippingTax
              );
              const finalizedOrder = {
                cart: cart,
                shippingCost: shippingCost,
              };
              beginCheckoutEvent(shippingCost);
              localStorage.setItem(
                "finalized_order",
                JSON.stringify(finalizedOrder)
              );
              dispatch({
                type: "UPDATE_FINALIZED_ORDER",
                value: finalizedOrder,
              });
              dispatch({ type: "CLEAR_CART" });
            })
            .then(() => callback({ should_proceed: true }));
        },
      })
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get("orderId");
    const orderKey = params.get("key");
    if (orderId && orderId) {
      payOrder({
        variables: {
          orderId: parseInt(orderId),
          orderKey: orderKey,
        },
      })
        .then(({ data: { payOrder: { htmlSnippet } = {} } }) => {
          displayCheckout(htmlSnippet);
        })
        .catch(() => window.localStorage.removeItem("woo-session"));
    } else if (cartExternalRevision > 0) {
      createKlarnaCheckoutOrder()
        .then(({ data }) => {
          displayCheckout(data.createKlarnaCheckoutOrder.htmlSnippet);
        })
        .catch(() => window.localStorage.removeItem("woo-session"));
    }
  }, [cartExternalRevision]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paymentMethod = searchParams.get("kco-external-payment");

    if (paymentMethod && paymentMethod === "vipps") {
      navigate(
        `${process.env.GATSBY_WORDPRESS_URL}/${checkoutUrl}/?${searchParams}`
      );
    }
  }, [location.search]);

  return null;
};

KlarnaCheckoutHooks.propTypes = {
  klarnaRef: PropTypes.shape({ current: PropTypes.elementType }),
};

export default KlarnaCheckout;

const GET_ORDER = gql`
  mutation getOrder {
    getKlarnaCheckoutOrder(input: {}) {
      billingAddress {
        city
        country
        email
        familyName
        givenName
        postalCode
        streetAddress
        region
        phone
      }
      shippingAddress {
        city
        country
        email
        familyName
        givenName
        phone
        postalCode
        region
        streetAddress
      }
      selectedShippingId
    }
  }
`;

const CREATE_KLARNA_CHECKOUT_ORDER = gql`
  mutation CreateKlarnaCheckoutOrder {
    createKlarnaCheckoutOrder(input: {}) {
      htmlSnippet
    }
  }
`;

const CHECKOUT = gql`
  mutation Checkout($input: CheckoutInput!) {
    checkout(input: $input) {
      order {
        shippingTotal
        shippingTax
        transactionId
      }
    }
  }
`;

const PAY_ORDER = gql`
  mutation payOrder($orderId: Int!, $orderKey: String!) {
    payOrder(input: { orderId: $orderId, orderKey: $orderKey }) {
      htmlSnippet
    }
  }
`;
